import React, { useEffect, useState, useMemo } from "react";
import { Play, X, Instagram, Youtube, Star, Send } from "lucide-react";
import { api } from "../lib/api";
import { parseVideoUrl } from "../lib/videoEmbed";

export default function Vitrine() {
  const [videos, setVideos] = useState([]);
  const [active, setActive] = useState(null);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/videos")
      .then((r) => setVideos(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => {
    const set = new Set(videos.map((v) => v.category).filter(Boolean));
    return ["all", ...Array.from(set).sort()];
  }, [videos]);

  const visible = useMemo(() => {
    if (filter === "all") return videos;
    return videos.filter((v) => v.category === filter);
  }, [videos, filter]);

  useEffect(() => {
    if (!active) return;
    const onKey = (e) => e.key === "Escape" && setActive(null);
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [active]);

  return (
    <div data-testid="page-vitrine" className="pt-24 md:pt-32 bg-[#FAF9F6] min-h-screen">
      {/* Header */}
      <header className="max-w-7xl mx-auto px-6 md:px-10 pb-10 md:pb-12">
        <div className="text-[11px] uppercase tracking-[0.28em] text-[#003F88] font-semibold">
          <span className="accent-line" />
          Vitrine de Vídeos
        </div>
        <div className="grid lg:grid-cols-12 gap-8 lg:items-end mt-6">
          <h1 className="lg:col-span-7 font-serif-display text-5xl md:text-7xl leading-[0.95] tracking-tight">
            Em <em className="italic font-light">imagem e voz</em>.
          </h1>
          <p className="lg:col-span-5 text-base md:text-lg text-neutral-600 font-light leading-relaxed">
            Reels, prévias e bastidores da campanha — direto do Instagram <span className="font-medium">@rosalvo_oficial</span> e dos canais oficiais.
            Pautas defendidas em vídeo, na linguagem do povo.
          </p>
        </div>
      </header>

      {/* Filters */}
      {categories.length > 1 && (
        <div className="max-w-7xl mx-auto px-6 md:px-10 mb-10">
          <div className="flex flex-wrap gap-2 md:gap-3">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                data-testid={`vitrine-filter-${cat}`}
                className={`text-xs uppercase tracking-widest px-4 py-2.5 transition-colors ${
                  filter === cat
                    ? "bg-[#003F88] text-white"
                    : "bg-white border border-neutral-200 text-neutral-700 hover:border-[#003F88] hover:text-[#003F88]"
                }`}
              >
                {cat === "all" ? "Todos" : cat}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Grid */}
      <div className="max-w-7xl mx-auto px-6 md:px-10 pb-24">
        {loading ? (
          <div className="text-center py-24 text-neutral-500">Carregando vídeos…</div>
        ) : visible.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {visible.map((v, idx) => (
              <VideoCard key={v.id} video={v} idx={idx} onClick={() => setActive(v)} />
            ))}
          </div>
        )}
      </div>

      {active && <VideoLightbox video={active} onClose={() => setActive(null)} />}
    </div>
  );
}

function VideoCard({ video, idx, onClick }) {
  const info = parseVideoUrl(video.video_url);
  const thumb = video.thumbnail || info.thumbnail;

  return (
    <button
      onClick={onClick}
      data-testid={`vitrine-card-${video.id}`}
      className="group relative overflow-hidden bg-neutral-900 aspect-[9/16] text-left reveal"
      style={{ animationDelay: `${idx * 0.04}s` }}
    >
      {thumb ? (
        <img
          src={thumb}
          alt={video.title}
          className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-70 group-hover:scale-[1.04] transition-all duration-500"
          loading="lazy"
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-[#003F88] to-[#002A5C]" />
      )}

      {/* Play overlay */}
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="w-14 h-14 md:w-16 md:h-16 bg-white/95 group-hover:bg-[#FFC72C] rounded-full flex items-center justify-center shadow-2xl transition-all group-hover:scale-110">
          <Play size={26} className="text-[#003F88] ml-1" fill="#003F88" />
        </span>
      </div>

      {/* Top badges */}
      <div className="absolute top-3 left-3 right-3 flex items-start justify-between">
        {video.featured && (
          <span className="inline-flex items-center gap-1 bg-[#FFC72C] text-[#003F88] text-[9px] font-bold uppercase tracking-widest px-2 py-1">
            <Star size={10} fill="currentColor" /> Destaque
          </span>
        )}
        {info.type === "instagram" && (
          <span className="bg-black/60 text-white text-[10px] uppercase tracking-widest px-2 py-1 ml-auto">
            <Instagram size={11} className="inline -mt-0.5 mr-1" /> Reels
          </span>
        )}
        {info.type === "youtube" && (
          <span className="bg-black/60 text-white text-[10px] uppercase tracking-widest px-2 py-1 ml-auto">
            <Youtube size={11} className="inline -mt-0.5 mr-1" /> YouTube
          </span>
        )}
      </div>

      {/* Bottom title */}
      <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/85 via-black/40 to-transparent">
        {video.category && (
          <div className="text-[#FFC72C] text-[10px] uppercase tracking-widest mb-1">{video.category}</div>
        )}
        <div className="text-white font-medium text-sm md:text-base leading-tight line-clamp-2">{video.title}</div>
      </div>
    </button>
  );
}

function VideoLightbox({ video, onClose }) {
  const info = parseVideoUrl(video.video_url);
  return (
    <div
      data-testid="vitrine-lightbox"
      className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center p-4 md:p-8"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        data-testid="vitrine-lightbox-close"
        aria-label="Fechar"
        className="absolute top-4 right-4 md:top-6 md:right-6 text-white/80 hover:text-white p-2 z-10"
      >
        <X size={26} />
      </button>

      <div className="w-full max-w-md md:max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="aspect-[9/16] bg-black">
          {info.type === "youtube" && (
            <iframe
              src={info.embedUrl + "&autoplay=1"}
              title={video.title}
              className="w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          )}
          {info.type === "instagram" && (
            <iframe
              src={info.embedUrl}
              title={video.title}
              className="w-full h-full bg-white"
              frameBorder="0"
              scrolling="no"
              allowtransparency="true"
              allowFullScreen
            />
          )}
          {info.type === "tiktok" && (
            <iframe
              src={info.embedUrl}
              title={video.title}
              className="w-full h-full"
              frameBorder="0"
              allow="autoplay; clipboard-write; encrypted-media"
              allowFullScreen
            />
          )}
          {info.type === "file" && (
            <video src={info.embedUrl} controls autoPlay className="w-full h-full object-contain bg-black" />
          )}
          {info.type === "unknown" && (
            <div className="flex items-center justify-center w-full h-full text-white text-center p-6">
              <div>
                <div className="text-sm uppercase tracking-widest text-white/60 mb-3">Vídeo</div>
                <a href={video.video_url} target="_blank" rel="noreferrer" className="text-[#FFC72C] underline">
                  Abrir vídeo em nova aba
                </a>
              </div>
            </div>
          )}
        </div>

        <div className="mt-4 text-white">
          {video.category && (
            <div className="text-[#FFC72C] text-[10px] uppercase tracking-widest mb-1">{video.category}</div>
          )}
          <h2 className="font-serif-display text-xl md:text-2xl leading-tight">{video.title}</h2>
          {video.description && (
            <p className="mt-2 text-sm text-white/80 leading-relaxed">{video.description}</p>
          )}
        </div>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="text-center py-24 max-w-xl mx-auto">
      <div className="font-serif-display text-3xl md:text-4xl text-neutral-900 mb-4">
        A vitrine <em className="italic font-light">está sendo preparada</em>.
      </div>
      <p className="text-neutral-600 leading-relaxed mb-8">
        Em breve, os vídeos temáticos da campanha — reels do Instagram, pautas defendidas em vídeo
        e bastidores — estarão aqui. Enquanto isso, siga <span className="font-medium">@rosalvo_oficial</span>.
      </p>
      <a
        href="https://www.instagram.com/rosalvo_oficial/"
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-3 bg-[#003F88] text-white px-7 py-4 text-xs uppercase tracking-[0.2em] font-medium hover:bg-[#002A5C] transition-colors"
      >
        <Send size={14} /> Ir para o Instagram
      </a>
    </div>
  );
}
